import { ReleaseReason } from '@/api/callcenter/assignments';
import { useReleaseAssignment } from '@/api/callcenter/assignments/releaseAssignment';
import { QueueItem } from '@/api/callcenter/dashboard';
import { FormSelect } from '@/components/Form';
import { Button } from '@/components/ui/elements/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/elements/dialog';
import { Form } from '@/components/ui/form/form';
import { toast } from '@/components/ui/elements/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type ReleaseLeadDialogProps = {
  assignment: QueueItem | null;
  onOpenChange: (open: boolean) => void;
  onReleased?: () => void;
  // Test/Storybook convenience ONLY -- pre-selects a reason via
  // react-hook-form defaultValues so the submit path can be exercised
  // without opening the Radix Select, which cannot be driven under jsdom
  // until the react-select version-skew is fixed (see the header comment in
  // ReleaseLeadDialog.test.tsx). Production callers never pass this: the
  // product rule is that an agent must actively choose a reason.
  defaultReason?: ReleaseReason;
};

// Labels are local to this component -- the reason *codes* are the shared
// contract with the API (see ReleaseReason), the copy shown to the agent is
// a display-only concern.
const RELEASE_REASON_OPTIONS: { label: string; value: ReleaseReason }[] = [
  { label: 'Language barrier', value: 'language_barrier' },
  { label: 'Duplicate lead', value: 'duplicate' },
  { label: 'Not a fit for our services', value: 'wrong_fit' },
  { label: 'Personal conflict', value: 'personal_conflict' },
  { label: 'Other', value: 'other' },
];

// A plain required string, not z.enum -- the Select's options above already
// constrain what value can land here, and a bare non-empty check keeps the
// "no reason pre-selected" starting value (an empty string) representable
// without fighting react-hook-form's defaultValues typing for a union with
// no empty member.
const schema = z.object({
  reason: z.string().min(1, 'Choose a reason before releasing this lead.'),
});

type ReleaseFormValues = z.infer<typeof schema>;

// Reason-only, audited release: a manager can see which reason an agent
// picked for every released lead, so this deliberately has no free-text note
// field and no reasonless skip. Release errors surface via the global toast
// (postReleaseAssignment does not opt out of it) -- there is no local error
// UI here by design.
export const ReleaseLeadDialog = ({
  assignment,
  onOpenChange,
  onReleased,
  defaultReason,
}: ReleaseLeadDialogProps) => {
  const releaseAssignment = useReleaseAssignment();

  const form = useForm<ReleaseFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: defaultReason ?? '' },
  });

  const isOpen = Boolean(assignment);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    // Reset ONLY on a genuine closed -> open transition -- deliberately NOT
    // keyed on the `assignment` object itself. AssignmentActivityTracker
    // re-derives its assignment from the polling dashboard query on every
    // render (queue.find), so each 30s poll / realtime invalidation hands
    // this component a fresh QueueItem reference for the same assignment
    // mid-open; an identity-keyed reset would silently wipe the agent's
    // chosen reason while they're mid-flow. The transition guard still
    // covers the case the reset exists for: a reason picked for a previous
    // lead, then cancelled without submitting, can't linger into the next
    // open (switching leads requires a close -> reopen at both call sites).
    // Resets back to defaultReason (test-only seam) rather than always '',
    // so the reset and the initial defaultValues can never disagree.
    if (isOpen && !wasOpenRef.current) {
      form.reset({ reason: defaultReason ?? '' });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, defaultReason, form]);

  const handleSubmit = (values: ReleaseFormValues) => {
    if (!assignment) return;

    releaseAssignment.mutate(
      {
        assignmentId: assignment.assignment_id,
        dto: { reason: values.reason as ReleaseReason },
      },
      {
        onSuccess: () => {
          toast({ title: 'Lead released' });
          onOpenChange(false);
          form.reset();
          onReleased?.();
        },
      },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Release this lead</DialogTitle>
          <DialogDescription>
            Your manager can see released leads and the reason you choose.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormSelect
              control={form.control}
              name="reason"
              label="Reason"
              placeholder="Choose a reason"
              options={RELEASE_REASON_OPTIONS}
            />

            <DialogFooter>
              <Button type="submit" disabled={releaseAssignment.isPending}>
                Release lead
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
